import { useParams, useNavigate } from 'react-router-dom';
import { Breadcrumb, LoadingSpinner, ProgressBar, IconButton } from '../components/ui';
import { PencilSquareIcon } from '@heroicons/react/24/outline';
import { ChartBarIcon } from '@heroicons/react/24/solid';
import { useClient, useClientPhases, useClientDocuments, useClientWorkshops, calcPhaseProgress } from '../hooks/useClients';
import ClientProfile from '../components/clients/ClientProfile';
import EngagementPhases from '../components/clients/EngagementPhases';
import DocumentLibrary from '../components/clients/DocumentLibrary';
import ClientSurveyTab from '../components/clients/ClientSurveyTab';
import ClientWorkshopsTab from '../components/clients/ClientWorkshopsTab';

const TABS = [
  { id: 'profile',    label: 'Profile' },
  { id: 'engagement', label: 'Engagement' },
  { id: 'documents',  label: 'Documents' },
  { id: 'surveys',    label: 'Surveys' },
  { id: 'workshops',  label: 'Workshops' },
];

const STATUS_CONFIG = {
  active:    { label: 'Active',    cls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  completed: { label: 'Completed', cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  paused:    { label: 'Paused',    cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
};

export default function ClientWorkspace() {
  const { id, tab } = useParams();
  const navigate = useNavigate();
  const validTabIds = TABS.map((t) => t.id);
  const activeTab = validTabIds.includes(tab) ? tab : 'profile';

  const { data: client, isLoading: clientLoading } = useClient(id);
  const { data: phases = [], isLoading: phasesLoading } = useClientPhases(id);
  const { data: documents = [], isLoading: docsLoading } = useClientDocuments(id);
  const { data: workshops = [], isLoading: workshopsLoading } = useClientWorkshops(id);
  if (clientLoading) {
    return (
      <div className="flex justify-center items-center min-h-[calc(100vh-80px)]">
        <LoadingSpinner />
      </div>
    );
  }

  if (!client) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="bg-danger-light border border-danger p-6 rounded-lg">
          <h3 className="text-lg font-semibold text-danger-dark mb-2">Client not found</h3>
          <button onClick={() => navigate('/clients')} className="text-brand-primary hover:underline text-sm">
            ← Back to Clients
          </button>
        </div>
      </div>
    );
  }

  // Compute summary metrics
  const allActivities = phases.flatMap((p) => p.activities || []);
  const allDeliverables = phases.flatMap((p) => p.deliverables || []);
  const overallProgress = calcPhaseProgress(allActivities, allDeliverables);
  const currentPhase = phases.find((p) => p.status === 'in_progress');
  const statusCfg = STATUS_CONFIG[client.consultingStatus] || STATUS_CONFIG.active;

  return (
    <div className="container mx-auto px-4 py-10">
      {/* Breadcrumb */}
      <Breadcrumb
        path={[
          { label: 'Clients', onClick: () => navigate('/clients') },
          { label: client.name, active: true },
        ]}
        className="mb-4"
      />

      {/* Client header */}
      <div className="flex flex-wrap gap-4 items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-3xl font-bold text-text-primary-light dark:text-text-primary">
              {client.name}
            </h1>
            <span className={`text-sm px-2.5 py-0.5 rounded-full font-medium ${statusCfg.cls}`}>
              {statusCfg.label}
            </span>
            {client.industry && (
              <span className="text-sm px-2.5 py-0.5 rounded-full bg-text-muted dark:bg-background-gray dark:text-text-secondary">
                {client.industry}
              </span>
            )}
          </div>
          {currentPhase && (
            <p className="text-sm text-text-muted-light dark:text-text-muted mt-1">
              Current phase: <span className="font-medium text-text-secondary-light dark:text-text-secondary">{currentPhase.name}</span>
            </p>
          )}
          <div className="flex gap-2 mt-3">
            <IconButton
              icon={ChartBarIcon}
              label="Insights"
              variant="secondary"
              size="md"
              display="icon"
              onClick={() => navigate(`/insights?view=company&id=${id}`)}
            />
            <IconButton
              icon={PencilSquareIcon}
              label="Edit in CMS"
              variant="secondary"
              size="md"
              display="icon"
              onClick={() => navigate(`/cms/companies/${id}`)}
            />
          </div>
        </div>

        {/* Engagement summary cards */}
        {!phasesLoading && phases.length > 0 && (
          <div className="flex gap-4 flex-wrap">
            <div className="text-center min-w-[80px]">
              <div className="text-2xl font-bold text-brand-primary">{overallProgress}%</div>
              <div className="text-xs text-text-muted-light dark:text-text-muted">Progress</div>
              <ProgressBar value={overallProgress} colorClass="bg-brand-primary" />
            </div>
            <div className="text-center min-w-[80px]">
              <div className="text-2xl font-bold text-text-primary-light dark:text-text-primary">
                {documents.length}
              </div>
              <div className="text-xs text-text-muted-light dark:text-text-muted">Documents</div>
            </div>
          </div>
        )}
      </div>

      {/* Tab nav */}
      <div className="border-b border-border-light dark:border-border mb-6">
        <div className="flex overflow-x-auto gap-0">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => navigate(`/clients/${id}/${tab.id}`)}
              className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-brand-primary text-brand-primary'
                  : 'border-transparent text-text-secondary-light dark:text-text-secondary hover:opacity-75'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      <div>
        {activeTab === 'profile' && <ClientProfile client={client} />}

        {activeTab === 'engagement' && (
          phasesLoading ? <LoadingSpinner /> :
          <EngagementPhases phases={phases} clientId={id} />
        )}

        {activeTab === 'documents' && (
          docsLoading ? <LoadingSpinner /> :
          <DocumentLibrary documents={documents} clientId={id} />
        )}

        {activeTab === 'surveys'   && <ClientSurveyTab clientId={id} />}
        {activeTab === 'workshops' && <ClientWorkshopsTab clientId={id} />}
      </div>
    </div>
  );
}
